/**
 * Which of Activity's three lanes is open, as a URL parameter.
 *
 * The convention the address page settled and `/alerts` and `/transactions/$id`
 * followed: a tab strip READS `?tab=` and WRITES it. Activity had neither — a
 * bare `defaultValue`, so the lane lived in component state only. Three things
 * were broken by that at once, and the heaviest lane suffered most: Audit (one
 * row per platform invocation, 408,000 in two months on a sandbox workspace) is
 * precisely the lane somebody wants to send to a colleague, and the link opened
 * Notifications. A reload did the same, and the Back button — having no history
 * entry to return to — did nothing at all.
 *
 * Split out of the page so the parsing is testable without mounting three lanes,
 * and so the route's `validateSearch` and the page agree on the vocabulary by
 * importing it rather than by both spelling it out. Same shape as
 * `features/transactions/transactionTabs.ts` and `addresses/tabApplies.ts`.
 */

export const ACTIVITY_TABS = ["notifications", "usage", "audit"] as const;

export type ActivityTab = (typeof ACTIVITY_TABS)[number];

/** The lane a reader lands on with no `?tab=`: the inbox, which is the cheap read. */
export const DEFAULT_ACTIVITY_TAB: ActivityTab = "notifications";

/**
 * The lane a URL hint names, or `undefined` when it names none.
 *
 * `undefined` rather than the default, because `validateSearch` uses this and a
 * defaulted value would write `?tab=notifications` into every plain `/activity`
 * link — making the bare address and the default lane two different URLs for
 * one page.
 */
export function activityTabFromHint(hint: unknown): ActivityTab | undefined {
  return typeof hint === "string" && (ACTIVITY_TABS as readonly string[]).includes(hint)
    ? (hint as ActivityTab)
    : undefined;
}

/** The lane to render: the hint when it names one, else the default. */
export function activityTab(hint: unknown): ActivityTab {
  return activityTabFromHint(hint) ?? DEFAULT_ACTIVITY_TAB;
}
